import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  RefreshCw, TrendingUp, Heart, MessageCircle, Send, Bookmark, Eye, Play,
} from "lucide-react";

const PALETTE_HEX = {
  cream: "#F5EFE6", coral_pastel: "#EEBBA8", terra_pastel: "#D3A58C",
  verde_pastel: "#C6D5BB", verde_agua: "#B7D2C8", azul_pastel: "#C4D4E0",
  azul_niebla: "#B4C3D0", rosa_pastel: "#F0D6D6", rosa_shell: "#EEC8C8",
  lila_pastel: "#D8C8E2", lila_dust: "#C6B8D2", moka_claro: "#D3C3AC",
  taupe: "#C2B39E", taupe_soft: "#D0C3B0", oat: "#DFD5C3", vanilla: "#EEE3CD",
  unknown: "#E5E5E5",
};

const KIND_COLORS = {
  post: "#B08D57", carrusel: "#DA5B3D", lifestyle: "#5B9B7A", reel: "#4169AF",
};

export default function InsightsPanel() {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  const load = async (forceRefresh = false) => {
    if (forceRefresh) setRefreshing(true); else setLoading(true);
    try {
      const url = forceRefresh
        ? `/meta/insights/dashboard?days=${days}&refresh=true`
        : `/meta/insights/dashboard?days=${days}`;
      const { data: d } = await api.get(url);
      setData(d);
      if (forceRefresh) toast.success(`Actualizado · ${d.total_items} posts`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando insights");
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (loading && !data) {
    return <div className="p-8 text-center text-gray-500">Cargando insights…</div>;
  }
  if (!data) return null;

  const items = data.items || [];
  const byPalette = data.by_palette || {};
  const byKind = data.by_kind || {};
  const byHour = data.by_hour || {};
  const topHooks = data.top_hooks || [];

  const paletteRows = Object.entries(byPalette)
    .sort((a, b) => (b[1].avg_engagement_rate || 0) - (a[1].avg_engagement_rate || 0));
  const kindRows = Object.entries(byKind)
    .sort((a, b) => (b[1].avg_engagement_rate || 0) - (a[1].avg_engagement_rate || 0));
  const hourRows = Object.entries(byHour)
    .map(([h, v]) => ({ hour: parseInt(h, 10), ...v }))
    .sort((a, b) => a.hour - b.hour);

  const maxHourInteractions = Math.max(1, ...hourRows.map((h) => h.interactions));
  const maxPaletteRate = Math.max(0.1, ...paletteRows.map(([, v]) => v.avg_engagement_rate || 0));

  return (
    <div className="space-y-6" data-testid="insights-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-serif italic text-[#1A1A1A]" data-testid="insights-title">
            Meta Insights · performance
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {data.total_items} publicaciones · últimos {data.range_days} días
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="px-3 py-2 text-sm border border-[#d9d2c6] rounded bg-white"
            data-testid="insights-days-select"
          >
            <option value="7">7 días</option>
            <option value="14">14 días</option>
            <option value="30">30 días</option>
            <option value="60">60 días</option>
            <option value="90">90 días</option>
          </select>
          <Button
            variant="outline" size="sm"
            onClick={() => load(true)}
            disabled={refreshing}
            data-testid="insights-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            Actualizar de Meta
          </Button>
        </div>
      </div>

      {/* Aggregates row: by kind */}
      <Card className="p-5">
        <h3 className="text-sm uppercase tracking-widest text-gray-500 mb-4">
          Por tipo de contenido
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kindRows.map(([kind, v]) => (
            <div
              key={kind}
              className="rounded-md p-3 border border-[#e6dfd2] bg-[#FAF6F0]"
              data-testid={`insights-kind-${kind}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ background: KIND_COLORS[kind] || "#999" }}
                />
                <span className="text-xs uppercase tracking-widest font-medium">{kind}</span>
              </div>
              <div className="text-2xl font-serif text-[#1A1A1A]">{v.count}</div>
              <div className="text-xs text-gray-500 mt-1">
                {v.reach.toLocaleString()} alcance · {v.interactions.toLocaleString()} interacciones
              </div>
              <div className="mt-2 text-xs">
                <span className="font-medium">ER: </span>
                <span style={{ color: KIND_COLORS[kind] || "#000" }}>
                  {(v.avg_engagement_rate || 0).toFixed(2)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Palette performance */}
      <Card className="p-5">
        <h3 className="text-sm uppercase tracking-widest text-gray-500 mb-4">
          Ranking por paleta Beyourself
        </h3>
        <div className="space-y-2">
          {paletteRows.map(([pal, v]) => (
            <div
              key={pal}
              className="flex items-center gap-3 py-2 border-b border-[#f0e9dc] last:border-0"
              data-testid={`insights-palette-${pal}`}
            >
              <div
                className="w-10 h-10 rounded-full border border-black/10 flex-shrink-0"
                style={{ background: PALETTE_HEX[pal] || "#ccc" }}
                title={pal}
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#1A1A1A]">{pal}</div>
                <div className="text-xs text-gray-500">{v.count} posts</div>
              </div>
              <div className="w-40 hidden md:block">
                <div className="h-2 bg-[#f0e9dc] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${((v.avg_engagement_rate || 0) / maxPaletteRate) * 100}%`,
                      background: PALETTE_HEX[pal] || "#B08D57",
                    }}
                  />
                </div>
              </div>
              <div className="w-16 text-right text-sm font-medium">
                {(v.avg_engagement_rate || 0).toFixed(2)}%
              </div>
              <div className="w-24 text-right text-xs text-gray-500 hidden md:block">
                {v.interactions.toLocaleString()} int.
              </div>
            </div>
          ))}
          {paletteRows.length === 0 && (
            <div className="text-sm text-gray-500 py-4 text-center">
              Sin datos aún. Publica más posts y vuelve en unas horas.
            </div>
          )}
        </div>
      </Card>

      {/* Best publishing hours */}
      <Card className="p-5">
        <h3 className="text-sm uppercase tracking-widest text-gray-500 mb-4">
          Rendimiento por hora de publicación
        </h3>
        <div className="grid grid-cols-12 md:grid-cols-24 gap-1">
          {Array.from({ length: 24 }).map((_, hour) => {
            const row = hourRows.find((r) => r.hour === hour) || { interactions: 0, count: 0 };
            const height = Math.max(8, (row.interactions / maxHourInteractions) * 60);
            return (
              <div key={hour} className="flex flex-col items-center" title={`${hour}:00 · ${row.count} posts · ${row.interactions} int.`}>
                <div
                  className="w-full rounded-t"
                  style={{ height: `${height}px`, background: row.count ? "#B08D57" : "#EEE" }}
                />
                <div className="text-[10px] text-gray-400 mt-1">{hour}</div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Top hooks */}
      <Card className="p-5">
        <h3 className="text-sm uppercase tracking-widest text-gray-500 mb-4">
          Top hooks (por interacciones)
        </h3>
        <div className="space-y-2" data-testid="insights-top-hooks">
          {topHooks.map((h, idx) => (
            <div
              key={h.id || idx}
              className="flex items-start gap-3 py-3 border-b border-[#f0e9dc] last:border-0"
            >
              <div className="w-8 text-center text-lg font-serif italic text-gray-400">
                {idx + 1}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-[#1A1A1A] leading-snug">{h.hook}</div>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: KIND_COLORS[h.kind] }} />
                    {h.kind}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full border" style={{ background: PALETTE_HEX[h.palette] || "#ccc" }} />
                    {h.palette}
                  </span>
                  <span>ER {(h.engagement_rate || 0).toFixed(2)}%</span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-lg font-serif text-[#1A1A1A]">
                  {h.interactions.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">int.</div>
              </div>
            </div>
          ))}
          {topHooks.length === 0 && (
            <div className="text-sm text-gray-500 py-4 text-center">
              Aún no hay datos.
            </div>
          )}
        </div>
      </Card>

      {/* Individual items table */}
      <Card className="p-5">
        <h3 className="text-sm uppercase tracking-widest text-gray-500 mb-4">
          Detalle por publicación
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="insights-items-table">
            <thead>
              <tr className="text-left text-gray-500 border-b border-[#e6dfd2]">
                <th className="py-2 pr-3">Kind</th>
                <th className="py-2 pr-3">Paleta</th>
                <th className="py-2 pr-3">Hook / Topic</th>
                <th className="py-2 pr-3 text-right"><Eye className="inline w-3 h-3" /></th>
                <th className="py-2 pr-3 text-right"><Heart className="inline w-3 h-3" /></th>
                <th className="py-2 pr-3 text-right"><MessageCircle className="inline w-3 h-3" /></th>
                <th className="py-2 pr-3 text-right"><Send className="inline w-3 h-3" /></th>
                <th className="py-2 pr-3 text-right"><Bookmark className="inline w-3 h-3" /></th>
                <th className="py-2 pr-3 text-right"><Play className="inline w-3 h-3" /></th>
                <th className="py-2 pr-3 text-right">ER%</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-[#f6f1e8] hover:bg-[#FAF6F0]">
                  <td className="py-2 pr-3">
                    <span
                      className="inline-block px-2 py-0.5 rounded text-white text-[10px] uppercase tracking-wider"
                      style={{ background: KIND_COLORS[it.kind] || "#999" }}
                    >
                      {it.kind}
                    </span>
                  </td>
                  <td className="py-2 pr-3">
                    <span
                      className="inline-block w-4 h-4 rounded-full border"
                      style={{ background: PALETTE_HEX[it.palette] || "#ccc" }}
                      title={it.palette}
                    />
                  </td>
                  <td className="py-2 pr-3 max-w-[300px] truncate">{it.hook || it.topic}</td>
                  <td className="py-2 pr-3 text-right">{it.reach.toLocaleString()}</td>
                  <td className="py-2 pr-3 text-right">{it.likes.toLocaleString()}</td>
                  <td className="py-2 pr-3 text-right">{it.comments.toLocaleString()}</td>
                  <td className="py-2 pr-3 text-right">{it.shares.toLocaleString()}</td>
                  <td className="py-2 pr-3 text-right">{it.saved.toLocaleString()}</td>
                  <td className="py-2 pr-3 text-right">{it.plays.toLocaleString()}</td>
                  <td className="py-2 pr-3 text-right font-medium">
                    {(it.engagement_rate || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && (
            <div className="text-sm text-gray-500 py-4 text-center">Sin publicaciones aún.</div>
          )}
        </div>
      </Card>

      <p className="text-xs text-gray-400 text-center py-2">
        Cache automático 6h · pulsa &quot;Actualizar de Meta&quot; para forzar refresh
      </p>
    </div>
  );
}
